import {
    Box,
    Grid,
    IconButton,
    SelectChangeEvent,
    Skeleton,
    Theme,
    Typography
} from "@mui/material";
import CustomDropdown from "./CustomDropdown";
import CustomePieChart from "../../../dashboard/chart/CustomePieChart";
import CircleIcon from '@mui/icons-material/Circle';
import { DropdownOption } from "../types";
import { filterType } from "../../../../redux-store/types/api-types";
import { ErrorText } from "../../../dashboard/chart/CustomeLineChart";
import { BlueRightIcon } from "../../../../assets/SvgIcons";
import ListModel from "../../../dashboard/chart/ListModel";
import { useState } from "react";
import { calculateOthersInPieChart } from "../../../../utils/utilityFunctions";
import { PieChartComponentLoading } from "../LoadingComponents";

const pieChartColor = ["#00A76F", "#FFAB00", "#3C92DC", "#3CA9DC", "#FF5630", "#0D3C6B"]


interface PieChartComponentProps {
    isLoading: boolean;
    selectedDropdownOption: string;
    handleDropdownValueChange: (event: SelectChangeEvent) => void;
    dropdownOptions: DropdownOption[];
    pieChartData: (string | number)[][];
    pieChartLabelData: filterType[];
    pieChartTotalValue: filterType;
    isSuccess: boolean;
    open: boolean;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>
    title?: string
}

export default function PieChartComponent({
    isLoading,
    selectedDropdownOption,
    handleDropdownValueChange,
    dropdownOptions,
    pieChartData,
    pieChartLabelData,
    pieChartTotalValue,
    isSuccess,
    open,
    setOpen,
    title
}: PieChartComponentProps): JSX.Element {
    const modifiedData = calculateOthersInPieChart({ labelData: pieChartLabelData, totalValue: pieChartTotalValue, data: pieChartData })
    return (
        <>
            {isLoading && (<PieChartComponentLoading title={title} />)}
            {(!isLoading && !isSuccess) && (
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            paddingBottom: "20px"
                        }}
                    >
                        <Typography
                            variant="h2"
                            sx={(theme: Theme) => ({
                                lineHeight: '25px',
                                letterSpacing: "0.01px",
                                color: theme.palette.text.primary,
                            })}
                        >
                            {title || "Asset Allocation"}
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            flex: 1,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                        }}
                    >
                        <Typography
                            variant="body_600"
                            sx={(theme: Theme) => ({
                                fontWeight: 500,
                                lineHeight: "22.5px",
                                letterSpacing: "0.01px",
                                color: theme.palette.text.primary,
                                textAlign: "center",
                                width: "100%"
                            })}
                        >
                            Something went wrong. Please try again.
                        </Typography>
                    </Box>
                </Box>

            )}
            {(!isLoading && isSuccess) && (
                <>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center"
                        }}
                    >
                        <Typography
                            variant="h2"
                            sx={(theme: Theme) => ({
                                lineHeight: "25px",
                                letterSpacing: "0.01px",
                                color: theme.palette.text.primary
                            })}
                        >
                            {title || "Asset Allocation"}
                        </Typography>
                        <CustomDropdown
                            isLoading={false}
                            selectedValue={selectedDropdownOption}
                            handleValueChange={handleDropdownValueChange}
                            dropdownOptions={dropdownOptions}
                        />
                    </Box>
                    {(
                        pieChartData?.length === 0 ||
                        pieChartLabelData?.length === 0 ||
                        Object.keys(pieChartTotalValue)?.length === 0
                    ) ?
                        <ErrorText /> :
                        <CustomePieChart
                            data={modifiedData?.data}
                            isLoading={false}
                            labelData={modifiedData?.labelData}
                            totalValue={pieChartTotalValue}
                            showSlider={false}
                            align='center'
                            title={title || "Asset Allocation"}
                        />
                    }
                    <Grid
                        container
                        sx={{ paddingRight: "10px" }}
                        rowSpacing={3}
                    >
                        {pieChartLabelData.length < 6 ? [...pieChartLabelData].map(({ label, value }: filterType, index: number) => (
                            <>
                                <Grid
                                    key={label}
                                    xs={6}
                                    item
                                    sx={{
                                        display: "flex",
                                        justifyContent: "flex-start",
                                        alignItems: "center",
                                        columnGap: "15px",
                                    }}
                                >
                                    <CircleIcon
                                        style={{
                                            color: pieChartColor[index],
                                            width: '11px',
                                            height: '11px',
                                        }}
                                    />
                                    <Typography
                                        variant="subtitle_400"
                                        sx={(theme: Theme) => ({
                                            lineHeight: "20px",
                                            color: theme.palette.text.primary
                                        })}
                                    >
                                        {label}
                                    </Typography>
                                </Grid>
                                <Grid
                                    xs={6}
                                    item
                                    sx={{
                                        display: "flex",
                                        justifyContent: "flex-end",
                                        alignItems: "center",
                                        columnGap: "15px"
                                    }}
                                >
                                    <Typography
                                        variant="subtitle_400"
                                        sx={(theme: Theme) => ({
                                            lineHeight: "20px",
                                            color: theme.palette.text.primary
                                        })}
                                    >
                                        {!Number.isNaN(Number(value ?? 0)) ?
                                            `${Math.round((Number(value ?? 0)) * 100) / 100}%` : '0%'}
                                    </Typography>
                                </Grid>
                            </>
                        )) : [...modifiedData?.labelData].slice(0, 6).map(({ label, value }: filterType, index: number) => (
                            <>
                                <Grid
                                    key={label}
                                    xs={6}
                                    item
                                    sx={{
                                        display: "flex",
                                        justifyContent: "flex-start",
                                        alignItems: "center",
                                        columnGap: "15px",
                                    }}
                                >
                                    <CircleIcon
                                        style={{
                                            color: 5 === index ? "transparent" : pieChartColor[index],
                                            width: '11px',
                                            height: '11px',
                                        }}
                                    />
                                    <Typography
                                        variant="subtitle_400"
                                        sx={(theme: Theme) => ({
                                            lineHeight: "20px",
                                            color: theme.palette.text.primary
                                        })}
                                    >
                                        {index === 5 ? 'Show More' : label}
                                    </Typography>
                                </Grid>
                                <Grid
                                    xs={6}
                                    item
                                    sx={{
                                        display: "flex",
                                        justifyContent: "flex-end",
                                        alignItems: "center",
                                        columnGap: "15px"
                                    }}
                                >
                                    {index === 5 ? <IconButton onClick={() => setOpen(true)}>
                                        <BlueRightIcon />
                                    </IconButton> :
                                        <Typography
                                            variant="subtitle_400"
                                            sx={(theme: Theme) => ({
                                                lineHeight: "20px",
                                                color: theme.palette.text.primary
                                            })}
                                        >
                                            {!Number.isNaN(Number(value ?? 0)) ?
                                                `${Math.round((Number(value ?? 0)) * 100) / 100}%` : '0%'}
                                        </Typography>
                                    }
                                </Grid>
                            </>
                        ))}
                    </Grid>
                </>
            )}
            <ListModel open={open} handleClose={() => setOpen(false)} labelData={[...(pieChartLabelData || [])]} title={title || "Asset Allocation"} />
        </>
    )
}